/**
 * Drawer genérico que muestra una explicación tras una respuesta incorrecta.
 * Lo usan stories (ActivitiesTab) y conversaciones (ConversationStartedFlow) —
 * el contenido se entrega ya resuelto a EN o ES por el caller (el helper
 * `pickExplanation` correspondiente). Mismo patrón visual de drawer que el
 * resto del área de alumno (p. ej. PlaybackSpeedDrawer): bottom en móvil,
 * right en desktop.
 *
 * Permite override de los labels para que cada flujo conserve su tono
 * (conversations: «Por qué no encaja del todo»; activities: «Por qué no es
 * correcta esta respuesta»); con fallback a defaults sensatos.
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/atoms/Button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
} from '@/components/molecules/Drawer';
import { useIsMobile } from '@/hooks/useIsMobile';
import styles from './WrongExplanationDrawer.module.scss';

export interface WrongExplanationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  explanation: string | null;
  /** Override del título del drawer (i18n string ya resuelto). Por defecto usa `conversation.explanationDrawerTitle`. */
  title?: string;
  /** Override del texto del botón de cierre (i18n string ya resuelto). Por defecto usa `conversation.closeExplanation`. */
  closeLabel?: string;
}

export function WrongExplanationDrawer({
  open,
  onOpenChange,
  explanation,
  title,
  closeLabel,
}: WrongExplanationDrawerProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const side = isMobile ? 'bottom' : 'right';

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        side={side}
        className={isMobile ? styles.drawerContentMobile : undefined}
        onSwipeToClose={isMobile ? () => onOpenChange(false) : undefined}
        closeSrLabel={t('header.close')}
        // Sin esto, Radix enfoca el botón que abrió el panel; en Safari/WebKit móvil el
        // siguiente toque no dispara click (parece «hay que pulsar varias veces»).
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DrawerHeader>
          <DrawerTitle>{title ?? t('conversation.explanationDrawerTitle')}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <p className={styles.body}>{explanation ?? ''}</p>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="primary" fullWidth type="button" onClick={() => onOpenChange(false)}>
            {closeLabel ?? t('conversation.closeExplanation')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
